import React, { useEffect, useState } from 'react';
import { Moment } from 'moment';
import { useForm } from 'react-hook-form';
import type { ModalProps } from '@mui/material';
import { Typography, Chip, Alert } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { CheckCircleOutline, CancelOutlined, CalendarTodayRounded } from '@mui/icons-material';

import { Modal, ModalContents, ModalTitle, ModalButton } from 'components/Modal/Modal';
import { controlledTextField } from 'components/ReactHookForm/controlledTextField';
import { getPaymentProcessors } from 'shared/processors';
import { DeedFileReader } from 'containers/DeedFileReader';
import Table from 'components/Table';
import Api, { ApiInstance } from 'entities/api';
import config from 'config';

import { extractPPGFCSVData } from '../../utils/extractCSVData';
import { STATUSES } from '../../shared/status';
import { PaymentProcessor } from '../../shared/types';

type TransactionStatus = STATUSES.PENDING | STATUSES.PPROCESSED | STATUSES.PROCESSING;

type FieldValues = {
  processedAt: Moment;
  data: ProcessorDataFields[];
  rawData: unknown;
  paymentProcessor: string;
};

type ProcessorDataFields = {
  transactionId: string;
  recipient: string;
  recipientName: string;
  uniqueIdentifier?: string | null;
  totalAmountCurrency: number;
  status: TransactionStatus;
  givingAccountWithdrawalForCoveredFees?: string;
};

const ProviderSelect = controlledTextField<FieldValues>()('paymentProcessor', {
  rules: { required: true },
});

const renderFileData = (
  selectedFileData: Pick<FieldValues, 'processedAt' | 'data'>,
  error: string | unknown
) => {
  if (!selectedFileData) {
    return <></>;
  }
  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box>
        <Typography variant="h6">File Content</Typography>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      <Alert severity="info">
        Please confirm the number of rows displayed here matches the expected row count.
      </Alert>
      <Stack mt={2} mb={3} direction="row">
        <Chip icon={<CalendarTodayRounded />} label={selectedFileData?.processedAt} />
        <Chip avatar={<Avatar>No</Avatar>} label={selectedFileData.data.length || 0} />
      </Stack>
      <Box style={{ maxHeight: 300, overflow: 'auto' }} mt={3}>
        <Table showHeader tableData={selectedFileData.data} />
      </Box>
    </Box>
  );
};

export const UpdateProcessedPaymentModal = ({
  style,
  onClickClose = (): void => {},
  ...props
}: {
  onClickClose: () => void;
} & Omit<ModalProps, 'children'>): JSX.Element => {
  const apiReq = new ApiInstance(String(config.donationBatchApiUrl));
  const [selectedFileData, setSelectedFileData] = useState<FieldValues | unknown>();
  const [errorMsg, setErrorMsg] = useState<string | unknown>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFile, setHasFile] = useState(false);
  const {
    handleSubmit: reactHookFormHandleSubmit,
    formState: { isValid, isSubmitting },
    control,
    setValue,
    reset,
  } = useForm<FieldValues>({
    mode: 'onChange',
    defaultValues: {
      processedAt: '',
      paymentProcessor: '',
    },
  });

  useEffect(() => {
    reset();
  }, [props.open]);

  const closeModalWindow = () => {
    setErrorMsg(null);
    setSelectedFileData(null);
    onClickClose();
  };

  const handleSubmit = reactHookFormHandleSubmit((data) => {
    try {
      setIsLoading(true);
      const extractedData = extractPPGFCSVData(data.rawData as unknown[]);
      if (!extractedData || !extractedData.processedAt || extractedData.data.length === 0) {
        setErrorMsg(
          'Data file contains some unexpected records. Please confirm it conforms to the expected format.'
        );
        setIsLoading(false);
        return;
      }

      apiReq
        .post(
          `payments/${data.paymentProcessor}/reconcile`,
          extractedData,
          {},
          {
            Authorization: Api.defaultHeaders.Authorization,
          }
        )
        .subscribe(
          () => {
            // eslint-disable-next-line no-alert
            alert(`${data.paymentProcessor} File processed successfully`);
            setIsLoading(false);
            closeModalWindow();
          },
          (error) => {
            if (error && error.responseBody) {
              setErrorMsg(error.responseBody);
            } else {
              setErrorMsg('Unknown error occurred while processing the file');
            }
            setIsLoading(false);
          }
        );
    } catch (er) {
      setErrorMsg(er);
      setIsLoading(false);
    }
  });

  const onFileDataRead = (data: unknown[]) => {
    const extractedData = extractPPGFCSVData(data);
    setSelectedFileData(extractedData);
  };

  return (
    <Modal
      disableAutoFocus
      disableEnforceFocus
      style={{
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      }}
      {...props}
    >
      <ModalContents
        onClickClose={closeModalWindow}
        body={
          <>
            <ModalTitle>Update Payments</ModalTitle>
            <Box paddingTop={4}>
              <form onSubmit={handleSubmit} disabled={isSubmitting}>
                <Stack spacing={3}>
                  <ProviderSelect
                    autoFocus={false}
                    controllerProps={{ control }}
                    helperText="Select a provider from the list"
                    label="Provider"
                    select
                  >
                    {getPaymentProcessors().map((processor: PaymentProcessor) => (
                      <MenuItem key={processor.name} value={processor.name}>
                        {processor.label}
                      </MenuItem>
                    ))}
                  </ProviderSelect>
                  <DeedFileReader
                    label=""
                    description="This file uploader expects a .xlsx file type"
                    onChange={(data) => {
                      setHasFile(data && data.length > 0);
                      setValue('rawData', data);
                      onFileDataRead(data);
                    }}
                    fileTypes={['.xlsx']}
                  />
                  {hasFile && renderFileData(selectedFileData, errorMsg)}
                </Stack>
              </form>
            </Box>
          </>
        }
        actions={
          <>
            <ModalButton
              variant="outlined"
              disabled={isSubmitting}
              onClick={(): void => {
                closeModalWindow();
              }}
              endIcon={<CancelOutlined />}
            >
              Cancel
            </ModalButton>
            &nbsp;
            <ModalButton
              disabled={!isValid || isSubmitting || isLoading || !hasFile}
              onClick={async (): Promise<void> => {
                try {
                  await handleSubmit();
                } catch (e) {
                  setErrorMsg(e);
                }
              }}
              endIcon={<CheckCircleOutline />}
            >
              {isLoading ? 'Processing...' : 'Start Processing'}
            </ModalButton>
          </>
        }
      />
    </Modal>
  );
};

export default UpdateProcessedPaymentModal;
